import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { getErrorMessage } from '../shared/thunk.utils'
import { ProjectMemberRole } from '../../lib/projectMember.types'
import type {
  MoveTaskTrackerBoardsItem,
  MoveTaskTrackerProjectsItem,
  TaskTrackerBoard,
  TaskTrackerProject,
} from '../domain.types'

export type CreateProjectDraftMember = {
  userId: number
  role: ProjectMemberRole
}

export type CreateProjectThunkPayload = {
  title: string
  members?: CreateProjectDraftMember[]
}

export type ReorderProjectsThunkPayload = {
  items: MoveTaskTrackerProjectsItem[]
  reorderedProjects: TaskTrackerProject[]
  previousProjects: TaskTrackerProject[]
}

export type ReorderProjectBoardsThunkPayload = {
  projectId: number
  items: MoveTaskTrackerBoardsItem[]
  reorderedBoards: TaskTrackerBoard[]
  previousBoards: TaskTrackerBoard[]
}

export const fetchProjectsThunk = createAsyncThunk(
  'taskTracker/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await taskTrackerApi.getProjects()
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось загрузить проекты'),
      )
    }
  },
)

export const createProjectThunk = createAsyncThunk(
  'taskTracker/createProject',
  async (payload: CreateProjectThunkPayload, { rejectWithValue }) => {
    try {
      const title = payload.title.trim()
      if (!title) {
        return rejectWithValue('Название проекта не может быть пустым')
      }

      return await taskTrackerApi.createProject({
        title,
        members: (payload.members ?? []).map((member) => ({
          user_id: member.userId,
          role_type: member.role,
        })),
      })
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось создать проект'),
      )
    }
  },
)

export const reorderProjectsThunk = createAsyncThunk(
  'taskTracker/reorderProjects',
  async (payload: ReorderProjectsThunkPayload, { rejectWithValue }) => {
    try {
      const updatedProjects = await taskTrackerApi.moveProjects(payload.items)
      return {
        reorderedProjects: payload.reorderedProjects,
        updatedProjects,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        previousProjects: payload.previousProjects,
        error: getErrorMessage(error, 'Не удалось изменить порядок проектов'),
      })
    }
  },
)

export const reorderProjectBoardsThunk = createAsyncThunk(
  'taskTracker/reorderProjectBoards',
  async (payload: ReorderProjectBoardsThunkPayload, { rejectWithValue }) => {
    try {
      const updatedBoards = await taskTrackerApi.moveBoards(
        payload.projectId,
        payload.items,
      )
      return {
        projectId: payload.projectId,
        reorderedBoards: payload.reorderedBoards,
        updatedBoards,
      }
    } catch (error: unknown) {
      return rejectWithValue({
        projectId: payload.projectId,
        previousBoards: payload.previousBoards,
        error: getErrorMessage(error, 'Не удалось изменить порядок досок'),
      })
    }
  },
)

export const deleteProjectThunk = createAsyncThunk(
  'taskTracker/deleteProject',
  async (
    payload: { projectId: number; password: string },
    { rejectWithValue },
  ) => {
    try {
      const result = await taskTrackerApi.deleteProject(
        payload.projectId,
        payload.password,
      )
      return { projectId: payload.projectId, deleted: result.deleted }
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Не удалось удалить проект'))
    }
  },
)
